import { Flex, Icon, Text } from '@chakra-ui/react';
import { FileCheck2Icon, FileWarningIcon } from 'lucide-react';
import { FC } from 'react';

/** One tab in the checklist-item document tab strip. */
export interface ChecklistDocumentTab {
  /** Ingestion document id — the selection key. */
  id: string;
  /** Display name shown on the tab. */
  documentName: string;
  /**
   * Count of open items on the document. When greater than zero the tab shows
   * a warning icon + count badge; otherwise a check icon. Omit when the surface
   * has no per-document count (the tab then always shows the check icon).
   */
  warningCount?: number;
}

interface ChecklistDocumentTabsProps {
  /** The documents under the active checklist item, one tab each. */
  tabs: ChecklistDocumentTab[];
  selectedDocumentId?: string;
  /**
   * Severity of a tab's open-item count. `warning` (amber) is the agent
   * compliance review default — agents see suggestions. `error` (red) is used
   * by the reviewer-facing embedded SkySlope drawer, where the counts are
   * hard validation errors and the harsher color is intended. Controls both
   * the per-tab icon and count-badge color when `warningCount` is positive.
   */
  tone?: 'warning' | 'error';
  /** Fill for the selected tab. Smart Assist passes `white`. */
  selectedTabBg?: string;
  /** Weight of the selected tab's label; inherited when omitted. */
  selectedTabFontWeight?: number;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * Center tab strip for a checklist item's documents — one tab per ingestion
 * document. The active tab is highlighted; switching tabs swaps the PDF (and,
 * where the surface wires it up, the document's issues). A single-document item
 * still renders its one tab. Scrolls horizontally when the tabs overflow.
 *
 * Shared across the agent compliance review page, the in-app compliance drawer,
 * and the embedded SkySlope drawer so the tab strip looks identical everywhere.
 */
export const ChecklistDocumentTabs: FC<ChecklistDocumentTabsProps> = ({
  tabs,
  selectedDocumentId,
  tone = 'warning',
  selectedTabBg = 'compliance.group.expanded.bg',
  selectedTabFontWeight,
  onSelectDocument,
}) => {
  if (tabs.length === 0) {
    return null;
  }

  // Color for a tab with open items — amber for agent-facing suggestions, red
  // for the reviewer drawer's hard validation errors.
  const countColor =
    tone === 'error' ? 'compliance.status.error' : 'compliance.status.warning';

  return (
    <Flex
      align="stretch"
      borderBottomColor="compliance.border.primary"
      borderBottomWidth={1}
      flexShrink={0}
      overflowX="auto"
      role="tablist"
      w="full"
    >
      {tabs.map((tab) => {
        const isSelected = tab.id === selectedDocumentId;
        const hasWarnings = (tab.warningCount ?? 0) > 0;

        return (
          <Flex
            key={tab.id}
            _hover={{
              bg: isSelected ? selectedTabBg : 'compliance.hover.bg',
            }}
            align="center"
            aria-selected={isSelected}
            bg={isSelected ? selectedTabBg : 'transparent'}
            borderRightColor="compliance.border.primary"
            borderRightWidth={1}
            cursor="pointer"
            flexShrink={0}
            gap={1.5}
            maxW="280px"
            px={5}
            py={4}
            role="tab"
            tabIndex={0}
            onClick={() => onSelectDocument(tab.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectDocument(tab.id);
              }
            }}
          >
            <Icon
              as={hasWarnings ? FileWarningIcon : FileCheck2Icon}
              boxSize="14px"
              color={
                hasWarnings
                  ? countColor
                  : isSelected
                    ? 'compliance.text.primary'
                    : 'compliance.text.muted'
              }
              flexShrink={0}
            />
            <Text
              color={
                isSelected ? 'compliance.text.primary' : 'compliance.text.muted'
              }
              fontSize="sm"
              fontWeight={isSelected ? selectedTabFontWeight : undefined}
              lineHeight="1"
              // Zero the margin so the label box is exactly its line height —
              // otherwise the browser's default `p { margin: 1em 0 }` (the
              // embedded drawer lives on the host SkySlope page, outside our
              // CSS reset) adds bottom margin that pushes the label below the
              // icon's center.
              margin={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {tab.documentName}
            </Text>
            {hasWarnings && (
              <Flex
                align="center"
                bg="whiteAlpha.100"
                borderRadius="6px"
                flexShrink={0}
                justify="center"
                px={1.5}
                py={1}
              >
                <Text
                  color={countColor}
                  fontSize="xs"
                  lineHeight="1"
                  // Zero the margin so the host SkySlope page's default
                  // `p { margin: 1em 0 }` doesn't add padding around the count
                  // (the embedded drawer renders outside our CSS reset).
                  margin={0}
                >
                  {tab.warningCount}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
